import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Spinner } from '../../../shared/components';
import { formatDateTime } from '../../../shared/utils/localization';
import { useAuth } from '../../auth/context/AuthContext';
import { PermissionScopes } from '../../auth/permissions';
import { formatMoney } from '../../invoices/invoiceForm';
import { cancelPayment, isOpenPayment, listPayments, sendPaymentLink, syncPayment } from '../services/paymentService';
import { PAYMENT_STATUSES, Payment, PaymentStatus } from '../types/Payment';
import { CreatePaymentModal } from './CreatePaymentModal';
import styles from '../Payments.module.css';

const statusClass = (status: PaymentStatus): string => {
  switch (status) {
    case 'Paid':
      return styles.statusPaid;
    case 'Failed':
      return styles.statusFailed;
    case 'Expired':
      return styles.statusExpired;
    case 'Canceled':
      return styles.statusCanceled;
    case 'AwaitingCustomer':
      return styles.statusAwaitingCustomer;
    default:
      return styles.statusPending;
  }
};

const providerLabel = (code: string, t: (key: string) => string): string =>
  code.toLowerCase() === 'pasha' ? t('providerPasha') : code;

export const PaymentsPage: React.FC = () => {
  const { t } = useTranslation(['payments', 'common']);
  const { hasPermission } = useAuth();
  const canManage = hasPermission(PermissionScopes.paymentsManage);
  const [status, setStatus] = React.useState<PaymentStatus | ''>('');
  const [page, setPage] = React.useState(1);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [items, setItems] = React.useState<Payment[]>([]);
  const [total, setTotal] = React.useState(0);
  const [hasNext, setHasNext] = React.useState(false);
  const [busyId, setBusyId] = React.useState<string | null>(null);
  const [createOpen, setCreateOpen] = React.useState(false);
  const [copiedId, setCopiedId] = React.useState<string | null>(null);
  const pageSize = 25;

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await listPayments({
        status: status || undefined,
        page,
        pageSize,
      });
      setItems(result.items);
      setTotal(result.total);
      setHasNext(result.hasNext);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [page, status, t]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const copyUrl = async (payment: Payment) => {
    if (!payment.checkoutUrl) return;
    try {
      await navigator.clipboard.writeText(payment.checkoutUrl);
      setCopiedId(payment.id);
      window.setTimeout(() => setCopiedId(current => (current === payment.id ? null : current)), 1600);
    } catch {
      setError(t('failedToSend'));
    }
  };

  const run = async (paymentId: string, work: () => Promise<Payment>, failKey: string) => {
    setBusyId(paymentId);
    setError(null);
    try {
      const updated = await work();
      setItems(current => current.map(item => (item.id === updated.id ? updated : item)));
    } catch (err) {
      setError(err instanceof Error ? err.message : t(failKey));
    } finally {
      setBusyId(null);
    }
  };

  const pages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className={styles.stack}>
      <div className={styles.headerRow}>
        <div className={styles.filters}>
          <label>
            {t('status')}
            <select
              value={status}
              onChange={event => {
                setPage(1);
                setStatus(event.target.value as PaymentStatus | '');
              }}
            >
              <option value="">{t('allStatuses')}</option>
              {PAYMENT_STATUSES.map(value => (
                <option key={value} value={value}>{t(`status${value}`)}</option>
              ))}
            </select>
          </label>
        </div>
        {canManage ? (
          <button className="btn btn-primary" type="button" onClick={() => setCreateOpen(true)}>
            {t('createPayment')}
          </button>
        ) : null}
      </div>

      {error ? <div className={styles.error}>{error}</div> : null}
      {loading ? <Spinner text={t('loading')} /> : null}
      {!loading && items.length === 0 ? <p className={styles.emptyState}>{t('empty')}</p> : null}

      {!loading && items.length > 0 ? (
        <div className={styles.sectionCard}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t('created')}</th>
                <th>{t('amount')}</th>
                <th>{t('status')}</th>
                <th>{t('description')}</th>
                <th>{t('checkoutUrl')}</th>
                <th>{t('actions')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map(item => (
                <tr key={item.id}>
                  <td>
                    <div>{formatDateTime(item.createdAtUtc)}</div>
                    <div className={styles.muted}>{providerLabel(item.providerCode, t)}</div>
                  </td>
                  <td>{formatMoney(item.amount, item.currency)}</td>
                  <td>
                    <span className={`${styles.statusBadge} ${statusClass(item.status)}`}>
                      {t(`status${item.status}`)}
                    </span>
                  </td>
                  <td>
                    <div>{item.description || '—'}</div>
                    {item.invoiceId ? (
                      <Link to={`/invoices/${item.invoiceId}`}>{t('invoiceLink')}</Link>
                    ) : null}
                  </td>
                  <td>
                    {item.checkoutUrl ? (
                      <a className={styles.checkoutUrl} href={item.checkoutUrl} target="_blank" rel="noreferrer">
                        {item.checkoutUrl}
                      </a>
                    ) : '—'}
                  </td>
                  <td>
                    <div className={styles.actionsCell}>
                      {item.checkoutUrl ? (
                        <button className="btn btn-secondary" type="button" disabled={busyId === item.id} onClick={() => void copyUrl(item)}>
                          {copiedId === item.id ? t('copied') : t('copyLink')}
                        </button>
                      ) : null}
                      {canManage && item.conversationId && isOpenPayment(item.status) ? (
                        <button
                          className="btn btn-secondary"
                          type="button"
                          disabled={busyId === item.id}
                          onClick={() => void run(item.id, () => sendPaymentLink(item.id), 'failedToSend')}
                        >
                          {t('sendLink')}
                        </button>
                      ) : null}
                      {canManage && isOpenPayment(item.status) ? (
                        <button
                          className="btn btn-secondary"
                          type="button"
                          disabled={busyId === item.id}
                          onClick={() => void run(item.id, () => syncPayment(item.id), 'failedToSync')}
                        >
                          {t('sync')}
                        </button>
                      ) : null}
                      {canManage && isOpenPayment(item.status) ? (
                        <button
                          className="btn btn-secondary"
                          type="button"
                          disabled={busyId === item.id}
                          onClick={() => {
                            if (window.confirm(t('confirmCancel'))) {
                              void run(item.id, () => cancelPayment(item.id), 'failedToCancel');
                            }
                          }}
                        >
                          {t('cancel')}
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className={styles.pagination}>
            <span className={styles.paginationMeta}>{t('pageOf', { page, pages })}</span>
            <div className={styles.paginationActions}>
              <button className="btn btn-secondary" type="button" disabled={page <= 1} onClick={() => setPage(current => current - 1)}>
                {t('previous')}
              </button>
              <button className="btn btn-secondary" type="button" disabled={!hasNext} onClick={() => setPage(current => current + 1)}>
                {t('next')}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {createOpen ? (
        <CreatePaymentModal
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            void load();
          }}
        />
      ) : null}
    </div>
  );
};
